import { GlobalConstants } from 'app/core/constants';
import { Globals } from 'app/core/globals';
import { Utils } from 'app/core/utils';
import { ConfigParser, ConfigParserError } from '../core/config/config-parser';
import { ImageStoreService } from '../core/imagestore/imagestore.service';
import { AdditionalInfoItemConfig, AdditionalInfoItemConfigPosition,
         CollageDesignerConfig, ConditionConfig,
         CopyRuleConfig, EditableArea, EditingViewConfig, ImageCropperSettings,
         OverlaysLayerConfig, OverlaysLayerEntryConfig, PartConfig, StepConfig,
         TemplateConfig, TemplateGroupConfig, TemplateLabelConfig,
         TemplatesSettings, TextPlaceholdersSettings, ImagePlaceholdersSettings
       } from './collage-designer-config';
import { CopyRules } from './copy-rules';
import { StageConfig } from './stage/stage-config';
import { StageConfigParser } from './stage/stage-config-parser';

export class CollageDesignerConfigParser extends ConfigParser
{
  private static readonly editableAreaDirections: Array<string> =
      ['x', 'y', 'width', 'height'];
  private productId: string;

  /**
   * @throws ConfigParserError
   */
  private static validateConfig(genericConfig: any): void
  {
    if (genericConfig.steps === undefined ||
        genericConfig.steps.length === 0) {
      throw new ConfigParserError('steps is undefined');
    }

    for (let step of genericConfig.steps) {
      if (step.parts === undefined) {
        throw new ConfigParserError(
            'step.parts of step "' + step.title + '" is undefined');
      }
      for (let partId in step.parts) {
        if (step.parts[partId]) {
          let partObj = step.parts[partId];

          if (partObj.editing_view === undefined) {
            throw new ConfigParserError(
                'editing view config in part "' + partId + '" is missing');
          }
          if (partObj.editing_view.type === undefined) {
            throw new ConfigParserError(
                'editing view\'s type in part "' + partId + '" is undefined');
          }
        }
      }
    }
  }

  private async getSteps(genericConfig: any): Promise<Array<StepConfig>>
  {
    const stepsObj = genericConfig.steps;
    let steps: Array<StepConfig> = [];

    for (let step of stepsObj) {
      let textPlaceholdersSettings =
        CollageDesignerConfigParser.getTextPlaceholdersSettings(step);

      // see #26979
      let allowMakeAutoTemplate =
        !!textPlaceholdersSettings.showCreateNewButton;

      let stepConfig = <StepConfig> {
        title: step.title,
        type: step.type,
        parts: await this.getParts(step.parts, allowMakeAutoTemplate),
        additionalInfoItems: step.additional_info_items
          ? CollageDesignerConfigParser.getAdditionalInfoItems(step)
          : [],
        textPlaceholdersSettings: textPlaceholdersSettings,
        imagePlaceholdersSettings:
            CollageDesignerConfigParser.getImagePlaceholdersSettings(step),
        imageCropperSettings:
            CollageDesignerConfigParser.getImageCropperSettings(step),
        templatesSettings:
            CollageDesignerConfigParser.getTemplatesSettings(step),

      };
      steps.push(stepConfig);
    }

    return steps;
  }

  private async getParts(partsObj: any,
                         allowMakeAutoTemplate: boolean
                        ): Promise<Array<PartConfig>>
  {
    let parts: Array<PartConfig> = [];

    for (let partId in partsObj) {
      if (partsObj[partId]) {
        let partObj = partsObj[partId];
        parts.push(<PartConfig> {
          id: partId,
          height: Utils.optional(partObj, ['dimensions', 'height']),
          width: Utils.optional(partObj, ['dimensions', 'width']),
          measuredHeight: Utils.optional(partObj, ['measurement', 'height']),
          measuredWidth: Utils.optional(partObj, ['measurement', 'width']),
          editingView: CollageDesignerConfigParser
            .getEditingViewConfig(partObj),
          templateGroups: await this
            .getTemplateGroups(partObj, partId, allowMakeAutoTemplate),
          copyRules: CollageDesignerConfigParser.getCopyRules(partObj, partId),
          previewType: partObj.preview_type,
          hasPreselectedTemplate: partObj.selected_template !== undefined,
          structureUrl: Utils.optional(partObj, ['structure_url']),
          templateAutoChange: true,
        });
      }
    }

    return parts;
  }

  private static getEditingViewConfig(genericPart: any): EditingViewConfig
  {
    return <EditingViewConfig> {
      type: genericPart.editing_view.type,
      overlays: CollageDesignerConfigParser.getOverlaysLayer(genericPart),
      reference: Utils.optional(genericPart, ['editing_view', 'reference']),
    };
  }

  private static getCopyRules(
    partObj: any, partId: string): Array<CopyRuleConfig>
  {
    const raw = partObj.copy_rules || [];

    return raw.map((el: any) => <CopyRuleConfig> {
      from: CopyRules.makePlace(el.from.part, el.from.item),
      to: CopyRules.makePlace(partId, el.to.item)
    });
  }

  private async getTemplateGroups(
    partObj: any,
    partId: string,
    allowMakeAutoTemplate: boolean): Promise<Array<TemplateGroupConfig>>
  {
    let templateGroups: Array<TemplateGroupConfig> = [];

    if (partObj.template_groups) {
      for (const templateGroupObj of partObj.template_groups) {
        const templates = CollageDesignerConfigParser
          .getTemplates(partObj, partId, templateGroupObj.templates);

        let templateGroupConfig = <TemplateGroupConfig> {
          id: templateGroupObj.id,
          title: templateGroupObj.title,
          templates: templates,
          templatesCount: Object.keys(templates).length,
          previewUrl: templateGroupObj.preview_url,
          conditions:
              CollageDesignerConfigParser.getConditions(templateGroupObj),
          isDefault: templateGroupObj.id === partObj.selected_template_group
        };
        templateGroups.push(templateGroupConfig);
      }
    }

    if ((templateGroups.length < 1 && allowMakeAutoTemplate) ||
        (Globals.mode === GlobalConstants.MODE_GOD &&
         partObj.selected_template === 'auto')) {
      templateGroups.push(<TemplateGroupConfig> {
        id: 'auto',
        title: 'auto',
        templates: {
          'auto': <TemplateConfig> {
            id: 'auto',
            previewUrl: undefined,
            title: 'auto',
            stageConfig: <StageConfig> {
              rotation: await this.guessRotation(partObj.structure_url),
              width: undefined,
              height: undefined,
              svgStructure: undefined,
              svgStructureURL: partObj.structure_url,
            },
            description: '',
            isDefault: true,
            tag: undefined,
            labels: [],
            imagePlaceholders: {landscape: 0, portrait: 0},
          }
        },
        templatesCount: 1,
        previewUrl: undefined,
        conditions: [],
        isDefault: true
      });
    }

    return templateGroups;
  }

  private async guessRotation(structureUrl: string): Promise<number>
  {
    const info = await this.imageStoreService.getImageInfo(structureUrl);
    return info.width >= info.height ? 0 : 90;
  }

  private static getTemplates(
    partObj: any, partId: string, templateIds: any
  ): {[templateId: string]: TemplateConfig}
  {
    let templates: {[templateId: string]: TemplateConfig} = {};

    for (let templateId of templateIds) {
      let templateObj = partObj.templates[templateId];
      if (templateObj === undefined) {
        throw new ConfigParserError(
            'configuration for template "' + templateId +
            '" in part "' + partId + '" not found');
      }

      try {
        let template = <TemplateConfig> {
          id: templateId,
          previewUrl: templateObj.preview_url,
          title: templateObj.title,
          stageConfig: StageConfigParser.getConfig(partObj, templateId),
          description: templateObj.description,
          isDefault: templateId === partObj.selected_template,
          tag: templateObj.tag,
          labels: CollageDesignerConfigParser.getTemplateLabels(
              templateObj.labels),
          imagePlaceholders: templateObj.changeable_images_count
        };
        templates[templateId] = template;
      }
      catch (e) {
        throw new ConfigParserError(
            'structure for template "' + templateId +  '" in part "' +
            partId + '" is invalid: ' + e);
      }
    }

    return templates;
  }

  private static getTemplateLabels(labelsObj: any): Array<TemplateLabelConfig>
  {
    let labels: Array<TemplateLabelConfig> = [];

    if (labelsObj) {
      for (const label of labelsObj) {
        labels.push(<TemplateLabelConfig> {
          id: label.id,
          value: label.value,
        });
      }
    }
    return labels;
  }

  private static getConditions(object: any): Array<ConditionConfig>
  {
    if (!object.conditions) {
      return [];
    }

    return object.conditions.map(c => <ConditionConfig>{
        type: c.type,
        value: c.value,
        part: c.part,
    });
  }

  private static getAdditionalInfoItems(
    step: any): Array<AdditionalInfoItemConfig>
  {
    let additionalInfoItems: Array<AdditionalInfoItemConfig> = [];

    if (step.additional_info_items) {
      for (const additionalInfoItem of step.additional_info_items) {
        additionalInfoItems.push(<AdditionalInfoItemConfig> {
          position: parseAddtionalInfoItemPosition(Utils.optional(
              additionalInfoItem, ['position'], 'design-area')),
          icon: Utils.optional(additionalInfoItem, ['icon']),
          label: additionalInfoItem.label,
          location: Utils.optional(additionalInfoItem, ['location']),
          autoDisplay: additionalInfoItem.auto_display ? true : false,
        });
      }
    }
    return additionalInfoItems;
  }

  private static getTextPlaceholdersSettings(
      step: any): TextPlaceholdersSettings
  {
    if (!step.text_placeholders) {
      step.text_placeholders = {};
    }
    return <TextPlaceholdersSettings>{
      showCreateNewButton: step.text_placeholders.show_create_new_button
        ? step.text_placeholders.show_create_new_button
        : false,
    };
  }

  private static getImagePlaceholdersSettings(
      step: any): ImagePlaceholdersSettings
  {
    if (!step.image_placeholders) {
      step.image_placeholders = {};
    }
    return <ImagePlaceholdersSettings>{
      disableImageRating: step.image_placeholders.disable_image_rating
        ? step.image_placeholders.disable_image_rating
        : false,
    };
  }

  private static getImageCropperSettings(step: any): ImageCropperSettings
  {
    if (!step.image_cropper) {
      step.image_cropper = {};
    }
    return <ImageCropperSettings>{
      showEditableAreaMargins: step.image_cropper.show_editable_area_margins
        ? step.image_cropper.show_editable_area_margins
        : false,
    };
  }

  private static getTemplatesSettings(step: any): TemplatesSettings
  {
    if (!step.templates) {
      step.templates = {};
    }
    return <TemplatesSettings>{
      showTemplatePreviewFrames: step.templates.show_template_preview_frames
        ? step.templates.show_template_preview_frames
        : false,
    };
  }

  /**
   * @throws ConfigParserError
   */
  private static getOverlaysLayer(genericPart: any): OverlaysLayerConfig
  {
    const genericEditingView = genericPart.editing_view;

    const overlays = CollageDesignerConfigParser.getOverlayElementsLayer(
      genericEditingView.overlays);

    let layerConfig: OverlaysLayerConfig = {
      editableArea: CollageDesignerConfigParser.getEditableArea(
          genericEditingView.editable_area),
      images: overlays.images,
      inlineSVGs: overlays.inlineSVGs,
    };

    return layerConfig;
  }

  private static getEditableArea(
      editableAreaConfig: any): EditableArea | undefined
  {
    const editableArea = {};
    if (!editableAreaConfig) {
      editableAreaConfig = {};
    }

    for (const direction of
        CollageDesignerConfigParser.editableAreaDirections) {
      if (editableAreaConfig[direction]) {
        editableArea[direction] = editableAreaConfig[direction];
      }
      else {
        return undefined;
      }
    }

    return <EditableArea>editableArea;
  }

  /**
   * @throws ConfigParserError
   */
  private static getOverlayElementsLayer(layers: any): any
  {
    let images: Array<OverlaysLayerEntryConfig> = [];
    let inlineSVGs: Array<OverlaysLayerEntryConfig> = [];

    for (let element of layers) {
      if (!element.location) {
          throw new ConfigParserError(
              'missing `location` for overlay element type: ' + element.type);
      }
      switch (element.type) {
        case 'image':
          images.push(<OverlaysLayerEntryConfig>{
            location: element.location,
            conditions: CollageDesignerConfigParser.getConditions(element)
          });
          break;
        case 'inline':
          inlineSVGs.push(<OverlaysLayerEntryConfig>{
            location: element.location,
            conditions: CollageDesignerConfigParser.getConditions(element)
          });
          break;
        default:
          throw new ConfigParserError(
              'unknown overlay element type: ' + element.type);
      }
    }

    return {
      images: images,
      inlineSVGs: inlineSVGs,
    };
  }

  public constructor(genericConfigObject: any,
                     productId: string,
                     private imageStoreService: ImageStoreService)
  {
    super(genericConfigObject);
    this.productId = productId;
  }

  /**
   * @throws ConfigParserError
   */
  public async getConfig(): Promise<CollageDesignerConfig>
  {
    CollageDesignerConfigParser.validateConfig(this.genericConfigObject);
    const config = <CollageDesignerConfig> {
      productId: this.productId,
      productStoreProductType:
          this.genericConfigObject.product_store_product_type,
      steps: await this.getSteps(this.genericConfigObject),
    };
    return config;
  }
}

function parseAddtionalInfoItemPosition(
    position: string): AdditionalInfoItemConfigPosition
{
  if (position === 'toolboxes') {
    return AdditionalInfoItemConfigPosition.toolboxes;
  }
  return AdditionalInfoItemConfigPosition.designArea;
}
